import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import LinkForm from './LinkForm';
import LinkIcon from '../svgIcons/LinkIcon';
import { SocialLinksApi } from '../../services';

const LinkModal = ({
    isOpen,
    onClose,
    onConfirm,
    mode = 'add', // 'add', 'edit', or 'match'
    initialData = null,
    existingLinks = [],
    isOwner = false,
    userId = null
}) => {
    const [currentView, setCurrentView] = useState('form');
    const [selectedLink, setSelectedLink] = useState(null);
    const [links, setLinks] = useState(existingLinks);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchSocialLinks = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const {
                data: { data }
            } = await SocialLinksApi.getSocialLinks(userId);
            // Transform API response: convert 'name' to 'title' and 'link' to 'url'
            const transformedLinks = (data || []).map(link => ({
                ...link,
                title: link.name,
                url: link.link,
                id: link.id || link._id
            }));
            setLinks(transformedLinks);
        } catch (err) {
            console.error('Error fetching social links:', err);
            setError('Failed to load social links');
            setLinks([]);
        } finally {
            setIsLoading(false);
        }
    }, [userId]);

    const handleClose = () => {
        setCurrentView('match');
        setSelectedLink(null);
        onClose();
    };

    const handleSelectLink = link => {
        if (isOwner) {
            setSelectedLink(link);
            setCurrentView('form');
        } else {
            // For non-owners, open the link in a new tab
            const transformedUrl = link.url.startsWith('http')
                ? link.url
                : `https://${link.url}`;
            window.open(transformedUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleFormSubmit = async values => {
        setIsLoading(true);
        setError(null);
        try {
            // Transform form data: convert 'title' to 'name' and 'url' to 'link'
            const apiData = {
                name: values.title,
                link: values.url
            };

            if (selectedLink && selectedLink.id) {
                // Update existing link
                await SocialLinksApi.updateSocialLink(
                    userId,
                    selectedLink.id,
                    apiData
                );
            } else {
                // Create new link
                await SocialLinksApi.createSocialLink(userId, apiData);
            }

            // Refresh the links list
            await fetchSocialLinks();

            // Call the onConfirm callback if provided
            if (onConfirm) {
                onConfirm(values);
            }

            handleClose();
        } catch (err) {
            console.error('Error saving social link:', err);
            setError(
                err?.response?.data?.message || 'Failed to save social link'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = async () => {
        if (!selectedLink || !selectedLink.id) return;

        setIsLoading(true);
        setError(null);
        try {
            await SocialLinksApi.deleteSocialLink(userId, selectedLink.id);
            // Refresh the links list
            await fetchSocialLinks();
            handleClose();
        } catch (err) {
            console.error('Error deleting social link:', err);
            setError(
                err?.response?.data?.message || 'Failed to delete social link'
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (mode === 'match') {
            setCurrentView('match');
        } else {
            setCurrentView('form');
        }
    }, [mode]);

    // Fetch social links when modal opens in match mode
    useEffect(() => {
        if (isOpen && mode === 'match' && userId) {
            fetchSocialLinks();
        }
    }, [isOpen, mode, userId, fetchSocialLinks]);

    if (!isOpen) return null;
    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center'>
            {/* Backdrop */}
            <div
                className='absolute inset-0 bg-black bg-opacity-50'
                onClick={handleClose}
            />

            {/* Modal Container */}
            <div className='relative bg-white rounded-[32px] px-1 py-8 mx-4 w-full max-w-lg shadow-lg border-[6px] border-[#4AFA0F]'>
                {/* Close Button */}
                <button
                    onClick={handleClose}
                    className='absolute top-1 right-2 text-gray-600 hover:text-gray-800 transition-colors bg-[#EDEDED] rounded-full p-4 w-[22px] h-[22px] flex items-center justify-center'
                >
                    ✕
                </button>

                {currentView === 'form' ? (
                    // Form View - Only show if owner
                    isOwner ? (
                        <>
                            {selectedLink && mode === 'match' && (
                                <button
                                    onClick={() => {
                                        setSelectedLink(null);
                                        setCurrentView('match');
                                    }}
                                    className='mb-4 text-gray-600 hover:text-gray-800 transition-colors text-sm font-medium flex items-center gap-1'
                                >
                                    ← Back to links
                                </button>
                            )}
                            <LinkForm
                                mode={selectedLink ? 'edit' : mode}
                                initialData={selectedLink || initialData}
                                onSubmit={handleFormSubmit}
                                onDeleteLink={
                                    selectedLink ? handleDeleteClick : null
                                }
                                isLoading={isLoading}
                            />
                        </>
                    ) : null
                ) : (
                    // Match Video Links View
                    <div className='pt-2 h-[400px]'>
                        {/* Title */}
                        <h2 className='text-[22px] font-poppins font-semibold text-black mb-6 text-center '>
                            Match Video Links
                        </h2>

                        {/* Links List */}
                        <div className='space-y-8 max-h-[410px] overflow-y-auto border-t border-gray-200 pt-2'>
                            {isLoading && (
                                <div className='text-center py-6 text-gray-500 my-[20%]'>
                                    Loading...
                                </div>
                            )}
                            {error && (
                                <div className='text-center py-6 text-red-500'>
                                    {error}
                                </div>
                            )}
                            {!isLoading && links && links.length > 0
                                ? links.map(link => (
                                      <div
                                          key={link.id}
                                          onClick={() => handleSelectLink(link)}
                                          className={`flex items-center justify-between px-4 pb-4 border-b-[2px] border-gray-300 ${
                                              isOwner
                                                  ? 'cursor-pointer hover:bg-gray-50'
                                                  : 'cursor-default'
                                          } transition-colors rounded-lg`}
                                      >
                                          <div className='flex items-center gap-3 flex-1'>
                                              <div className='w-6 h-6 flex items-center justify-center flex-shrink-0'>
                                                  <LinkIcon
                                                      width={24}
                                                      height={24}
                                                  />
                                              </div>
                                              <div className='flex-1'>
                                                  <div className='font-semibold text-black text-[14px]'>
                                                      {link.title}
                                                  </div>
                                                  <div className='text-gray-500 text-[12px] truncate'>
                                                      {link.url}
                                                  </div>
                                              </div>
                                          </div>
                                          <div className='ml-2'>
                                              <svg
                                                  className='w-5 h-5 text-gray-400'
                                                  fill='none'
                                                  stroke='currentColor'
                                                  viewBox='0 0 24 24'
                                              >
                                                  <path
                                                      strokeLinecap='round'
                                                      strokeLinejoin='round'
                                                      strokeWidth={2}
                                                      d='M9 5l7 7-7 7'
                                                  />
                                              </svg>
                                          </div>
                                      </div>
                                  ))
                                : !isLoading && (
                                      <div className='text-center py-6 text-gray-500 flex flex-col items-center gap-8 my-[20%]'>
                                          <LinkIcon />
                                          This user has no match video links
                                          yet.
                                      </div>
                                  )}
                        </div>

                        {/* Add a website Button - Only for owner */}
                        {isOwner && (
                            <button
                                onClick={() => setCurrentView('form')}
                                className='w-full mt-6 py-3 flex items-center bg-transparent outline-none hover:outline-none border-none justify-start gap-4  transition-opacity'
                            >
                                <div className='w-[30px] h-[30px] min-w-[30px] rounded-full bg-[#52FF00] flex items-center justify-center'>
                                    <span className='text-[30px] font-light text-black'>
                                        +
                                    </span>
                                </div>
                                <span className='text-[18px] font-semibold text-black'>
                                    Add a website
                                </span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LinkModal;
